import { trackEvent } from '../../utils/analytics-v2'
import { logBreadcrumb } from '../../utils/monitorClient'
import { FeedbackFormValues, INITIAL_FEEDBACK_VALUES } from './types'

const FEEDBACK_STORAGE_PREFIX = 'veho_feedback_'

/** Retrieves persisted feedback form values from localStorage for the given tracking ID. */
export function getSavedFormValues(trackingId: string): FeedbackFormValues | null {
  try {
    const saved = localStorage.getItem(`${FEEDBACK_STORAGE_PREFIX}${trackingId}`)
    if (saved) return { ...INITIAL_FEEDBACK_VALUES, ...JSON.parse(saved) }
  } catch (error) {
    logBreadcrumb('Failed to read saved feedback form values', { trackingId, error })
  }
  return null
}

/** Persists current feedback form values to localStorage for the given tracking ID. */
export function saveFormValues(trackingId: string, values: FeedbackFormValues): void {
  try {
    localStorage.setItem(`${FEEDBACK_STORAGE_PREFIX}${trackingId}`, JSON.stringify(values))
  } catch (error) {
    logBreadcrumb('Failed to save feedback form values', { trackingId, error })
  }
}

/** Removes persisted feedback form values from localStorage for the given tracking ID. */
export function clearSavedFormValues(trackingId: string): void {
  try {
    localStorage.removeItem(`${FEEDBACK_STORAGE_PREFIX}${trackingId}`)
  } catch (error) {
    logBreadcrumb('Failed to clear saved feedback form values', { trackingId, error })
  }
}

interface FeedbackQuestion {
  questionId: string
  questionDisplayName: string
  answer: string
  questionType: 'rating' | 'multiSelect' | 'boolean' | 'text'
}

/** Converts feedback form values into a structured array of question/answer pairs for submission. */
export function buildFeedbackPayload(values: FeedbackFormValues): FeedbackQuestion[] {
  const questions: FeedbackQuestion[] = [
    {
      questionId: 'overallDeliveryExperience',
      questionDisplayName: 'Overall Delivery Experience',
      answer: String(values.overallDeliveryExperience ?? ''),
      questionType: 'rating',
    },
    {
      questionId: 'negativeExperienceReasons',
      questionDisplayName: 'Negative Experience Reasons',
      answer: JSON.stringify(values.negativeExperienceReasons),
      questionType: 'multiSelect',
    },
    {
      questionId: 'driverSatisfaction',
      questionDisplayName: 'Driver Satisfaction',
      answer: String(values.driverSatisfaction ?? ''),
      questionType: 'boolean',
    },
    {
      questionId: 'driverIssueReasons',
      questionDisplayName: 'Driver Issue Reasons',
      answer: JSON.stringify(values.driverIssueReasons),
      questionType: 'multiSelect',
    },
    {
      questionId: 'trackingExperience',
      questionDisplayName: 'Tracking Experience',
      answer: String(values.trackingExperience ?? ''),
      questionType: 'boolean',
    },
    {
      questionId: 'trackingIssueReasons',
      questionDisplayName: 'Tracking Issue Reasons',
      answer: JSON.stringify(values.trackingIssueReasons),
      questionType: 'multiSelect',
    },
    {
      questionId: 'additionalDeliveryComments',
      questionDisplayName: 'Additional Delivery Comments',
      answer: values.additionalDeliveryComments,
      questionType: 'text',
    },
    {
      questionId: 'supportExperience',
      questionDisplayName: 'Support Experience',
      answer: String(values.supportExperience ?? ''),
      questionType: 'rating',
    },
    {
      questionId: 'supportIssueReasons',
      questionDisplayName: 'Support Issue Reasons',
      answer: JSON.stringify(values.supportIssueReasons),
      questionType: 'multiSelect',
    },
    {
      questionId: 'additionalSupportComments',
      questionDisplayName: 'Additional Support Comments',
      answer: values.additionalSupportComments,
      questionType: 'text',
    },
    {
      questionId: 'consentToFollowUp',
      questionDisplayName: 'Consent to Follow Up',
      answer: String(values.consentToFollowUp),
      questionType: 'boolean',
    },
  ]

  return questions.filter(q => q.answer !== '' && q.answer !== '[]')
}

/** Fires a feedback_form_submitted analytics event with the consumer's responses. */
export function trackFeedbackSubmission(
  values: FeedbackFormValues,
  trackingId: string,
  hadSupportInteraction: boolean
) {
  trackEvent('feedback_form_submitted', {
    trackingId,
    hadSupportInteraction,
    overallDeliveryExperience: values.overallDeliveryExperience,
    negativeExperienceReasons: values.negativeExperienceReasons,
    driverSatisfaction: values.driverSatisfaction,
    trackingExperience: values.trackingExperience,
    trackingIssueReasons: values.trackingIssueReasons,
    supportExperience: values.supportExperience,
    supportIssueReasons: values.supportIssueReasons,
  })
}
